import React, {
  ElementType,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  Stack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  Icon,
  Skeleton,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  LinkBox,
  LinkOverlay,
  Portal,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoBusiness } from "react-icons/io5";
import about_us from "./about_us.svg";
import career from "./career.svg";
import faqs from "./faqs.svg";
import contact_us from "./contact_us.svg";
import BrandLogo from "../../assets/logos/brand.svg";
import WhiteLogo from "../../assets/logos/white.svg";
import clsx from "clsx";
import { BiX } from "react-icons/bi";
import { useAuth0, User } from "@auth0/auth0-react";
import { LoggedInMenu } from "./logged-in-menu";
import { gql, useLazyQuery } from "@apollo/client";
import { LoggedInMenuContent } from "./logged-in-menu-content";
import { IoHome } from "react-icons/io5";
import { GrUserWorker } from "react-icons/gr";
import { RiCalendarCheckFill } from "react-icons/ri";
import { useApplicationConfig } from "../../context/ApplicationConfig";
import { resetAnalyticsUser } from "../../helpers/analytics";
import BusinessThumbnail from "../../assets/artisan-observing-shipment.jpeg";
import HomeownersThumbnail from "../../assets/navigation/thumbnails/homeowner.jpg";
import SkilledWorkersThumbnail from "../../assets/navigation/thumbnails/skilled-worker.jpg";
import CommunityManagerThumbnail from "../../assets/navigation/thumbnails/community.jpg";
import { PageSection } from "../PageSection";
import { useEmbed } from "../../hooks/useEmbed";

const GET_PROFILES = gql`
  query GetUserProfiles {
    myPro {
      id
    }
    myClient {
      id
    }
    myHomeowner {
      id
    }
  }
`;

type NavbarTheme =
  | "default"
  | "flatteredFlamingo"
  | "prussianBlue"
  | "lawnGreen";

interface NavbarThemeProps {
  bg: string;
  textColor: string;
  inactiveTextColor: string;
  logo: string;
  borderColor: string;
  btn: {
    colorScheme: string;
    bg: string;
    textColor: string;
  };
}

const colors: Record<NavbarTheme, NavbarThemeProps> = {
  flatteredFlamingo: {
    bg: "flatteredFlamingo.500",
    textColor: "white",
    inactiveTextColor: "white",
    logo: WhiteLogo,
    borderColor: "white",
    btn: {
      colorScheme: "white",
      bg: "white",
      textColor: "flatteredFlamingo.500",
    },
  },
  prussianBlue: {
    bg: "prussianBlue.500",
    textColor: "white",
    inactiveTextColor: "white",
    logo: WhiteLogo,
    borderColor: "white",
    btn: {
      colorScheme: "white",
      bg: "white",
      textColor: "prussianBlue.500",
    },
  },
  lawnGreen: {
    bg: " lawnGreen.900",
    textColor: "white",
    inactiveTextColor: "white",
    logo: WhiteLogo,
    borderColor: "white",
    btn: {
      colorScheme: "white",
      bg: "white",
      textColor: " lawnGreen.900",
    },
  },
  default: {
    bg: "#FBFBFB",
    textColor: "black",
    inactiveTextColor: "inactiveTextColor",
    logo: BrandLogo,
    borderColor: "gray-900",
    btn: {
      colorScheme: "flatteredFlamingo",
      bg: "flatteredFlamingo.500",
      textColor: "white",
    },
  },
};

interface NavbarProps {
  banner?: ElementType;
  colorScheme?: NavbarTheme;
}

interface NavbarLinkProps {
  isActive: boolean;
  url: string;
  themeProps: NavbarThemeProps;
  text: string;
}

const menu = {
  homepage: {
    url: "/",
  },
  business: {
    url: "/business",
  },
  homeOwners: {
    url: "/homeowners",
  },
  artisans: {
    url: "/artisans",
  },
  aboutUs: {
    url: "/about",
  },
  career: {
    url: "https://laborhack.grovehr.com/careers",
  },
  faqs: {
    url: "/faqs",
  },
  contactUs: {
    url: "/contact",
  },
  blog: {
    url: "https://blog.laborhack.com/",
  } /*
  materials: {
    url: "https://bit.ly/40n5Pv8",
  },*/,
  login: {
    url: "/auth/login",
  },
  register: {
    url: "/auth/register",
  },
  subscriptions: {
    url: "/subscriptions",
  },
  whatsApp: {
    url: "https://wa.me/2349020000000",
  },
};

const NotLoggedInMenu = ({
  theme,
}: PropsWithChildren<{ theme: NavbarThemeProps }>) => (
  <Flex
    alignItems={"center"}
    justify="end"
    display={{
      base: "none",
      md: "none",
      lg: "none",
      xl: "flex",
      width1279px: "none",
      width1428px: "flex",
    }}
    gap={4}
  >
    <Button
      as={RouterLink}
      to={menu.login.url}
      height="auto"
      borderColor={theme.borderColor}
      color={theme.textColor}
      variant="outline"
      py={3}
      px={5}
      border="2px solid"
      fontSize={20}
      fontStyle="normal"
      fontWeight="500"
      _hover={{ textDecoration: "none", color: theme.textColor }}
    >
      Login
    </Button>
    <Button
      as={RouterLink}
      to={menu.register.url}
      height="auto"
      borderColor={theme.btn.colorScheme}
      colorScheme={theme.btn.colorScheme}
      bg={theme.btn.bg}
      color={theme.btn.textColor}
      py={3}
      px={5}
      border="2px solid"
      fontSize={20}
      fontStyle="normal"
      fontWeight="500"
      _hover={{ textDecoration: "none", color: theme.btn.textColor }}
    >
      Sign Up
    </Button>
  </Flex>
);

const UserMenu = ({
  isClient,
  isPro,
  isLoading,
  theme,
  user,
  logout,
}: PropsWithChildren<{
  isClient: boolean;
  isPro: boolean;
  isLoading?: boolean;
  theme: NavbarThemeProps;
  user?: User;
  logout: () => void;
}>) => {
  if (isLoading) {
    return <Skeleton height="6.75rem" />;
  }

  if (!user) return <NotLoggedInMenu theme={theme} />;

  return (
    <LoggedInMenu
      user={user!}
      isClient={isClient}
      isPro={isPro}
      logout={logout}
    />
  );
};

const NavbarLink = ({ isActive, url, themeProps, text }: NavbarLinkProps) => {
  return (
    <Link
      as={RouterLink}
      to={url}
      color={isActive ? themeProps.textColor : themeProps.inactiveTextColor}
      fontSize="20px"
      fontStyle="normal"
      fontWeight={500}
      lineHeight="28px"
      _hover={{ textDecoration: "none", color: themeProps.textColor }}
      className={clsx(
        `border-b-4 border-transparent hover:border-${themeProps.borderColor}`,
        isActive && `border-${themeProps.borderColor}`,
      )}
    >
      <Text>{text}</Text>
    </Link>
  );
};

const OldNavbar: React.FC<NavbarProps> = ({ banner: Banner, colorScheme }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  const [searchingForExistingAccessToken, setSearchingForExistingAccessToken] =
    useState(false);

  const { isLoading, isAuthenticated, user, getAccessTokenSilently, logout } =
    useAuth0();

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setSearchingForExistingAccessToken(true);

      getAccessTokenSilently()
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setSearchingForExistingAccessToken(false);
        });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const [getMyProfiles, getMyProfilesHandle] = useLazyQuery<{
    myPro: any;
    myClient: any;
  }>(GET_PROFILES);

  useEffect(() => {
    if (isAuthenticated) {
      getMyProfiles();
    }
  }, [isAuthenticated]);

  const isActivePage = (urlToCheck: string) => {
    return location.pathname === urlToCheck;
  };

  const handleLogout = () => {
    resetAnalyticsUser();
    logout({ returnTo: window.location.origin });
  };

  const theme = colors[colorScheme || "default"];

  const companyMenuActive =
    isActivePage(menu.aboutUs.url) ||
    isActivePage(menu.faqs.url) ||
    isActivePage(menu.contactUs.url);

  const servicesMenuActive =
    isActivePage(menu.business.url) ||
    isActivePage(menu.homeOwners.url) ||
    isActivePage(menu.artisans.url) ||
    isActivePage(menu.subscriptions.url);

  const profiles = getMyProfilesHandle.data;

  return (
    <Stack bg={theme.bg} position="sticky" width="full" zIndex={999} top={0}>
      <Flex
        px={{
          base: 3,
          lg: 0,
        }}
        h={16}
        alignItems={"center"}
        justifyContent={"space-between"}
        height="6.75rem"
        m={{ base: 0, lg: "0 75px" }}
        display={isOpen ? "none" : "flex"}
      >
        <RouterLink to="/">
          <Image src={theme.logo} alt="LaborHack" height="24px" width="174px" />
        </RouterLink>
        <Flex
          display={{
            base: "none",
            md: "none",
            lg: "flex",
            xl: "flex",
            width1279px: "flex",
            width1428px: "flex",
          }}
          alignItems="center"
          justify="center"
          maxWidth="60%"
          margin="auto"
          gap={8}
        >
          <Menu>
            <MenuButton
              as={Button}
              variant="link"
              rightIcon={<IoIosArrowDown />}
              bgColor="transparent"
              color={
                servicesMenuActive ? theme.textColor : theme.inactiveTextColor
              }
              fontSize="20px"
              fontStyle="normal"
              fontWeight={500}
              lineHeight="28px"
              textDecoration="none"
              _hover={{ textDecoration: "none", color: theme.textColor }}
              className={clsx(
                "border-b-4 border-transparent hover:border-white",
                companyMenuActive && "border-white",
              )}
            >
              Services
            </MenuButton>
            <MenuList
              color="white"
              display="Grid"
              width="612px"
              position="relative"
              right={{
                base: "21rem",
                md: "21rem",
                lg: "21rem",
                xl: "21rem",
                "2xl": "21rem",
              }}
              top="21px"
              boxShadow="-1px 12px 34px -17px rgba(183, 183, 183, 0.70)"
              zIndex={9999}
              css={{
                "@media (min-width: 1280px) and (max-width: 1375px)": {
                  right: "-21rem",
                },
              }}
            >
              <MenuItem color="black" onClick={() => {}} gridColumn="1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={RouterLink}
                    to="/business"
                    spacing={18}
                    role="group"
                  >
                    <Icon as={IoBusiness} w={6} h={6} color={"#A8A8A8"} />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        For Businesses
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{ color: "blue.300" }}
                      >
                        Hire certified artisans for your project
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" gridColumn="2">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={RouterLink}
                    to="/homeowners"
                    spacing={18}
                    role="group"
                  >
                    <Icon as={IoHome} w={6} h={6} color={"#A8A8A8"} />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        For Homeowners
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{ color: "blue.300" }}
                      >
                        Hire certified artisans for your home needs
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="1/span1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={RouterLink}
                    to="/skilled-workers"
                    spacing={18}
                    role="group"
                  >
                    <Icon as={GrUserWorker} w={6} h={6} color={"#A8A8A8"} />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        For Skilled Workers
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{ color: "blue.300" }}
                      >
                        Get hired and earn more
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="2">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={RouterLink}
                    to="/subscriptions"
                    spacing={18}
                    role="group"
                  >
                    <Icon
                      as={RiCalendarCheckFill}
                      w={6}
                      h={6}
                      color={"#A8A8A8"}
                    />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        Subscriptions
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{ color: "blue.300" }}
                      >
                        Scheduled home and facility maintenance services
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>
          <Menu>
            <MenuButton
              as={Button}
              variant="link"
              rightIcon={<IoIosArrowDown />}
              bgColor="transparent"
              color={
                companyMenuActive ? theme.textColor : theme.inactiveTextColor
              }
              fontSize="20px"
              fontStyle="normal"
              fontWeight={500}
              lineHeight="28px"
              textDecoration="none"
              _hover={{ textDecoration: "none", color: theme.textColor }}
              className={clsx(
                "border-b-4 border-transparent hover:border-white",
                companyMenuActive && "border-white",
              )}
            >
              Company
            </MenuButton>
            <MenuList
              color="white"
              display="Grid"
              width="612px"
              position="relative"
              right={{
                base: "21rem",
                md: "21rem",
                lg: "21rem",
                xl: "21rem",
                "2xl": "21rem",
              }}
              top="21px"
              boxShadow="-1px 12px 34px -17px rgba(183, 183, 183, 0.70)"
              zIndex={9999}
              css={{
                "@media (min-width: 1280px) and (max-width: 1375px)": {
                  right: "-21rem",
                },
              }}
            >
              <MenuItem color="black" onClick={() => {}} gridColumn="1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack as={RouterLink} to="/about" spacing={18} role="group">
                    <Image src={about_us} alt="About Us" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        About Us
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{
                          color: "blue.300",
                        }}
                      >
                        Learn more about who we are
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" gridColumn="2">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={"a"}
                    href="https://laborhack.grovehr.com/careers"
                    spacing={18}
                    role="group"
                  >
                    <Image src={career} alt="Career" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        Career
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{
                          color: "blue.300",
                        }}
                      >
                        Join the LaborHack team
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="1/span1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack as={RouterLink} to="/faqs" spacing={18} role="group">
                    <Image src={faqs} alt="Faqs" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        FAQs
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{
                          color: "blue.300",
                        }}
                      >
                        Get answers to your questions
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="2/span 1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={RouterLink}
                    to="/contact"
                    spacing={18}
                    role="group"
                  >
                    <Image src={contact_us} alt="Contact Us" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        Contact Us
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                        color={"#5E5E5E"}
                        _groupHover={{
                          color: "blue.300",
                        }}
                      >
                        Reach out to us anytime
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>
          <Link
            href="https://blog.laborhack.com/"
            color={
              isActivePage(menu.blog.url)
                ? theme.textColor
                : theme.inactiveTextColor
            }
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
            _hover={{ textDecoration: "none", color: theme.textColor }}
            className={clsx(
              "border-b-4 border-transparent hover:border-white",
              isActivePage(menu.blog.url) && "border-white",
            )}
          >
            <Text>Blog</Text>
          </Link>
        </Flex>

        <Flex
          display={{
            base: "none",
            md: "none",
            lg: "flex",
            xl: "flex",
            width1279px: "flex",
            width1428px: "flex",
          }}
        >
          <UserMenu
            isClient={!!profiles?.myClient}
            isPro={!!profiles?.myPro}
            isLoading={isLoading || searchingForExistingAccessToken}
            user={user}
            theme={theme}
            logout={handleLogout}
          />
        </Flex>

        <IconButton
          display={{
            base: "flex",
            md: "flex",
            lg: "none",
            xl: "none",
            width1279px: "none",
            width1428px: "none",
          }}
          onClick={() => onOpen()}
          icon={<HamburgerIcon boxSize={6} />}
          aria-label={""}
          bgColor="transparent"
          color={theme.btn.bg}
        />
      </Flex>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay>
          <DrawerContent>
            <div className="h-dvh bg-white flex flex-col">
              <div className="flex justify-between items-center p-4">
                <Image
                  src="./new-logo.svg"
                  alt="LaborHack"
                  height="24px"
                  width="174px"
                />
                <IconButton
                  isRound={true}
                  variant="outline"
                  aria-label="close"
                  fontSize="24px"
                  borderColor="gray.300"
                  icon={
                    <Icon
                      as={BiX}
                      w={{
                        base: 6,
                        lg: 8,
                      }}
                      h={{
                        base: 6,
                        lg: 8,
                      }}
                    />
                  }
                  onClick={onClose}
                />
              </div>
              <div className="flex-1 py-4">
                <Stack spacing={0}>
                  <Accordion allowToggle>
                    <AccordionItem
                      borderBottom="1px solid #F3F3F3"
                      borderTop="1px solid #F3F3F3"
                    >
                      <AccordionButton p={0} pr={4}>
                        <Box
                          flex="1"
                          textAlign="left"
                          p="16px"
                          color="#0E0302"
                          fontFamily="AeonikPro"
                          fontSize="18px"
                          fontStyle="normal"
                          fontWeight={500}
                          lineHeight="24px"
                          _hover={{ textDecoration: "none", color: "Inherit" }}
                        >
                          Services
                        </Box>
                        <AccordionIcon as={IoIosArrowDown} />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={RouterLink}
                            to="/business"
                            spacing={18}
                            _hover={{
                              textDecoration: "none",
                              color: "inherit",
                            }}
                            onClick={onClose}
                          >
                            <Icon
                              as={IoBusiness}
                              w={6}
                              h={6}
                              color={"#A8A8A8"}
                            />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                For Businesses
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Hire certified artisans for your project
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={RouterLink}
                            to="/homeowners"
                            spacing={18}
                            onClick={onClose}
                          >
                            <Icon as={IoHome} w={6} h={6} color={"#A8A8A8"} />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                For Homeowners
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Hire certified artisans for your home needs
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={RouterLink}
                            to="/skilled-workers"
                            spacing={18}
                            onClick={onClose}
                          >
                            <Icon
                              as={GrUserWorker}
                              w={6}
                              h={6}
                              color={"#A8A8A8"}
                            />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                For Skilled Workers
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Get hired and earn more
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" p="16px">
                          <HStack
                            spacing={18}
                            as={RouterLink}
                            to="/subscriptions"
                            onClick={onClose}
                          >
                            <Icon
                              as={RiCalendarCheckFill}
                              w={6}
                              h={6}
                              color={"#A8A8A8"}
                            />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                Subscriptions
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Scheduled home and facility maintenance services
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                  <Accordion allowToggle>
                    <AccordionItem
                      borderBottom="1px solid #F3F3F3"
                      borderTop={"none"}
                    >
                      <AccordionButton p={0} pr={4}>
                        <Box
                          flex="1"
                          textAlign="left"
                          p="16px"
                          color="#0E0302"
                          fontFamily="AeonikPro"
                          fontSize="18px"
                          fontStyle="normal"
                          fontWeight={500}
                          lineHeight="24px"
                          _hover={{ textDecoration: "none", color: "Inherit" }}
                        >
                          Company
                        </Box>
                        <AccordionIcon as={IoIosArrowDown} />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={RouterLink}
                            to="/about"
                            spacing={18}
                            _hover={{
                              textDecoration: "none",
                              color: "inherit",
                            }}
                            onClick={onClose}
                          >
                            <Image src={about_us} alt="About Us" />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                About Us
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Learn more about who we are
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={"a"}
                            href="https://laborhack.grovehr.com/careers"
                            spacing={18}
                            onClick={onClose}
                          >
                            <Image src={career} alt="Career" />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                Career
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Join the LaborHack team
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" onClick={() => {}} p="16px">
                          <HStack
                            as={RouterLink}
                            to="/faqs"
                            spacing={18}
                            onClick={onClose}
                          >
                            <Image src={faqs} alt="Faqs" />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                FAQs
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Get answers to your questions
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                        <Flex color="black" p="16px">
                          <HStack
                            spacing={18}
                            as={RouterLink}
                            to="/contact"
                            onClick={onClose}
                          >
                            <Image src={contact_us} alt="Contact Us" />
                            <Box>
                              <Text
                                fontSize="14px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="21px"
                              >
                                {" "}
                                Contact Us
                              </Text>
                              <Text
                                fontSize="12px"
                                fontStyle="normal"
                                fontWeight="500"
                                lineHeight="19px"
                                color="#5E5E5E"
                              >
                                Reach out to us anytime
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                  <Link
                    as={RouterLink}
                    to={menu.blog.url}
                    borderBottom="1px solid #F3F3F3"
                    p="16px"
                    fontFamily="AeonikPro"
                    fontSize="18px"
                    fontStyle="normal"
                    fontWeight={500}
                    lineHeight="24px"
                    _hover={{ textDecoration: "none", color: "Inherit" }}
                    onClick={onClose}
                  >
                    Blog
                  </Link>

                  {!user && (
                    <Link
                      as={RouterLink}
                      to={menu.login.url}
                      p="16px"
                      borderBottom="1px solid #F3F3F3"
                      fontFamily="AeonikPro"
                      fontSize="18px"
                      fontStyle="normal"
                      fontWeight={500}
                      lineHeight="24px"
                      _hover={{ textDecoration: "none", color: "inherit" }}
                      onClick={onClose}
                    >
                      Login
                    </Link>
                  )}
                </Stack>
              </div>
              <div className="flex flex-col p-4">
                {!user ? (
                  <Button
                    as={RouterLink}
                    to={menu.register.url}
                    colorScheme="flatteredFlamingo"
                    size="lg"
                  >
                    Sign Up
                  </Button>
                ) : (
                  <Accordion allowToggle>
                    <AccordionItem>
                      <AccordionButton p={0} pr={4}>
                        <Box
                          flex="1"
                          textAlign="left"
                          p="16px"
                          color="#0E0302"
                          fontFamily="AeonikPro"
                          fontSize="18px"
                          fontStyle="normal"
                          fontWeight={500}
                          lineHeight="24px"
                          _hover={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Flex alignItems="center" gap={4}>
                            <Avatar
                              size="sm"
                              name={user?.name}
                              src={user?.picture}
                            />
                            <h5 className="text-lg">
                              Hi, {user?.given_name || user.nickname} <br />
                            </h5>
                          </Flex>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <LoggedInMenuContent
                          email={user?.email || ""}
                          isClient={!!profiles?.myClient}
                          isPro={!!profiles?.myPro}
                          logout={handleLogout}
                          className="px-0"
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
            </div>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
      {Banner && <Banner width="full" display={isOpen ? "none" : "flex"} />}
    </Stack>
  );
};

const structure = [
  {
    title: "Find Talent",
    items: [
      {
        title: "Businesses",
        url: "/businesses",
        img: BusinessThumbnail,
      },
      {
        title: "Homeowners",
        url: "/homeowners",
        img: HomeownersThumbnail,
      },
    ],
  },
  {
    title: "Get Jobs",
    items: [
      {
        title: "Skilled Workers",
        url: "/skilled-workers",
        img: SkilledWorkersThumbnail,
      },
    ],
  },
  {
    title: "Manage Talent",
    items: [
      {
        title: "Community",
        url: "/community-manager",
        img: CommunityManagerThumbnail,
      },
    ],
  },
  {
    title: "Company",
    items: [
      {
        title: "About Us",
        url: "/about",
      },
      // {
      //   title: "Careers",
      //   url: "https://laborhack.grovehr.com/careers",
      // },
      {
        title: "FAQs",
        url: "/faqs",
      },
      {
        title: "Blog",
        url: "https://blog.laborhack.com/",
      },
      {
        title: "Contact Us",
        url: "/contact",
      },
    ],
  },
];

const NewNavbar: React.FC<NavbarProps> = ({ banner: Banner, colorScheme }) => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { toggles } = useApplicationConfig();

  const { isAuthenticated, user, logout } = useAuth0();

  const [getMyProfiles, getMyProfilesHandle] = useLazyQuery<{
    myPro: any;
    myClient: any;
    myHomeowner: any;
  }>(GET_PROFILES);

  useEffect(() => {
    if (isAuthenticated) {
      getMyProfiles();
    }
  }, [isAuthenticated, getMyProfiles]);

  const isActivePage = (urlToCheck: string) => {
    return location.pathname === urlToCheck;
  };

  const handleLogout = () => {
    resetAnalyticsUser();
    logout({ returnTo: window.location.origin });
  };

  const profiles = getMyProfilesHandle.data;
  const isClientProfile = toggles?.enable_client_profile_on_orders
    ? !!profiles?.myHomeowner
    : !!profiles?.myClient;

  return (
    <>
      <PageSection className="bg-prussianBlue-500 border-b border-white">
        <div className="flex justify-between items-stretch min-h-24 px-5 md:px-0">
          <div className="flex items-center gap-4">
            <RouterLink to="/">
              <Image src={WhiteLogo} alt="LaborHack" height={22} />
            </RouterLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex justify-between items-stretch gap-12">
            {structure.map((item) =>
              "url" in item ? (
                <LinkBox
                  as="button"
                  key={item.title}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  h="auto"
                  color="prussianBlue.100"
                  fontWeight={500}
                  _hover={{
                    textDecoration: "none",
                    color: "white",
                    borderBottomColor: "white",
                  }}
                  borderY="3px solid transparent"
                >
                  <LinkOverlay
                    _hover={{
                      textDecoration: "none",
                      color: "white",
                    }}
                  >
                    {item.title}
                  </LinkOverlay>
                </LinkBox>
              ) : (
                <div
                  key={item.title}
                  className="flex h-auto flex-col justify-center gap-2"
                >
                  <Popover placement="bottom-start">
                    {({ isOpen }) => (
                      <>
                        <PopoverTrigger>
                          <Box
                            as="button"
                            display="flex"
                            alignItems="center"
                            gap={2}
                            h="auto"
                            fontWeight={500}
                            color="prussianBlue.100"
                          >
                            <p>{item.title}</p>
                            {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                          </Box>
                        </PopoverTrigger>
                        <Portal>
                          <PopoverContent
                            borderRadius={0}
                            border="none"
                            boxShadow="none"
                          >
                            <PopoverBody className="flex flex-col gap-2 p-0 bg-prussianBlue-50">
                              {item.items?.map((item) => {
                                const isActive = isActivePage(item.url);
                                return (
                                  <LinkBox
                                    key={item.title}
                                    className="py-2 flex items-center gap-4 relative"
                                  >
                                    {"img" in item && (
                                      <Image
                                        src={item.img}
                                        alt={item.title}
                                        height={12}
                                        width={16}
                                        objectFit="cover"
                                      />
                                    )}
                                    <p className=" text-prussianBlue-500 font-medium">
                                      <LinkOverlay
                                        as={
                                          item.url.startsWith("http")
                                            ? "a"
                                            : RouterLink
                                        }
                                        {...(item.url.startsWith("http")
                                          ? {
                                              href: item.url,
                                              target: "_blank",
                                              rel: "noopener noreferrer",
                                            }
                                          : { to: item.url })}
                                        _hover={{
                                          textDecoration: "none",
                                          color: "prussianBlue.500",
                                        }}
                                      >
                                        {item.title}
                                      </LinkOverlay>
                                    </p>
                                    {isActive && (
                                      <Box
                                        position="absolute"
                                        right={4}
                                        width="6px"
                                        height="6px"
                                        borderRadius="50%"
                                        bg="prussianBlue.500"
                                      />
                                    )}
                                  </LinkBox>
                                );
                              })}
                            </PopoverBody>
                          </PopoverContent>
                        </Portal>
                      </>
                    )}
                  </Popover>
                </div>
              ),
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex border-l border-white">
            {!isAuthenticated ? (
              <>
                <LinkBox
                  as="button"
                  color="white"
                  h="auto"
                  px={16}
                  fontWeight={600}
                >
                  <LinkOverlay
                    as={RouterLink}
                    to={menu.login.url}
                    _hover={{
                      textDecoration: "none",
                      color: "white",
                    }}
                  >
                    Login
                  </LinkOverlay>
                </LinkBox>
                <LinkBox
                  as="button"
                  color="white"
                  bgColor="flatteredFlamingo.500"
                  h="auto"
                  px={16}
                  fontWeight={600}
                >
                  <LinkOverlay
                    as={RouterLink}
                    to={menu.register.url}
                    _hover={{
                      textDecoration: "none",
                      color: "white",
                    }}
                  >
                    Sign Up
                  </LinkOverlay>
                </LinkBox>
              </>
            ) : (
              <div className="flex items-center px-4">
                <LoggedInMenu
                  user={user!}
                  isClient={isClientProfile}
                  isPro={!!profiles?.myPro}
                  logout={handleLogout}
                />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center">
            <IconButton
              onClick={onOpen}
              icon={<HamburgerIcon boxSize={6} />}
              aria-label="Open menu"
              bgColor="transparent"
              color="white"
              _hover={{ bg: "prussianBlue.400" }}
            />
          </div>
        </div>
      </PageSection>

      {/* Mobile Menu Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay>
          <DrawerContent>
            <div className="h-dvh bg-white flex flex-col">
              {/* Mobile Header */}
              <div className="flex justify-between items-center p-4 bg-prussianBlue-500">
                <RouterLink to="/" onClick={onClose}>
                  <Image
                    src={WhiteLogo}
                    alt="LaborHack"
                    height="24px"
                    width="174px"
                  />
                </RouterLink>
                <IconButton
                  isRound={true}
                  variant="outline"
                  aria-label="close"
                  fontSize="24px"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: "prussianBlue.400" }}
                  icon={<Icon as={BiX} w={6} h={6} />}
                  onClick={onClose}
                />
              </div>

              {/* Mobile Menu Content */}
              <div className="flex-1 py-4">
                <Stack spacing={0}>
                  {structure.map((section) => (
                    <Accordion key={section.title} allowToggle>
                      <AccordionItem
                        borderBottom="1px solid #F3F3F3"
                        borderTop={
                          section.title === "Find Talent"
                            ? "1px solid #F3F3F3"
                            : "none"
                        }
                      >
                        <AccordionButton p={0} pr={4}>
                          <Box
                            flex="1"
                            textAlign="left"
                            p="16px"
                            color="#0E0302"
                            fontFamily="AeonikPro"
                            fontSize="18px"
                            fontStyle="normal"
                            fontWeight={500}
                            lineHeight="24px"
                            _hover={{
                              textDecoration: "none",
                              color: "inherit",
                            }}
                          >
                            {section.title}
                          </Box>
                          <AccordionIcon as={IoIosArrowDown} />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          {section.items?.map((item) => (
                            <Flex key={item.title} color="black" p="16px">
                              <HStack
                                as={
                                  item.url.startsWith("http") ? "a" : RouterLink
                                }
                                {...(item.url.startsWith("http")
                                  ? {
                                      href: item.url,
                                      target: "_blank",
                                      rel: "noopener noreferrer",
                                    }
                                  : { to: item.url })}
                                spacing={18}
                                _hover={{
                                  textDecoration: "none",
                                  color: "inherit",
                                }}
                                onClick={onClose}
                                w="full"
                              >
                                {"img" in item && (
                                  <Image
                                    src={item.img}
                                    alt={item.title}
                                    width="48px"
                                    height="36px"
                                    objectFit="cover"
                                    borderRadius="4px"
                                  />
                                )}
                                <Box>
                                  <Text
                                    fontSize="14px"
                                    fontStyle="normal"
                                    fontWeight="500"
                                    lineHeight="21px"
                                  >
                                    {item.title}
                                  </Text>
                                </Box>
                              </HStack>
                            </Flex>
                          ))}
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  ))}
                </Stack>
              </div>

              {/* Mobile Auth Section */}
              <div className="flex flex-col p-4 border-t border-gray-200">
                {!isAuthenticated ? (
                  <Stack spacing={3}>
                    <Button
                      as={RouterLink}
                      to={menu.login.url}
                      variant="outline"
                      colorScheme="prussianBlue"
                      size="lg"
                      onClick={onClose}
                    >
                      Login
                    </Button>
                    <Button
                      as={RouterLink}
                      to={menu.register.url}
                      colorScheme="flatteredFlamingo"
                      size="lg"
                      onClick={onClose}
                    >
                      Sign Up
                    </Button>
                  </Stack>
                ) : (
                  <Accordion allowToggle>
                    <AccordionItem border="none">
                      <AccordionButton p={0} pr={4}>
                        <Box
                          flex="1"
                          textAlign="left"
                          p="16px"
                          color="#0E0302"
                          fontFamily="AeonikPro"
                          fontSize="18px"
                          fontStyle="normal"
                          fontWeight={500}
                          lineHeight="24px"
                          _hover={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Flex alignItems="center" gap={4}>
                            <Avatar
                              size="sm"
                              name={user?.name}
                              src={user?.picture}
                            />
                            <h5 className="text-lg">
                              Hi, {user?.given_name || user?.nickname}
                            </h5>
                          </Flex>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <LoggedInMenuContent
                          email={user?.email || ""}
                          isClient={isClientProfile}
                          isPro={!!profiles?.myPro}
                          logout={handleLogout}
                          className="px-0"
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
            </div>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>

      {Banner && <Banner width="full" />}
    </>
  );
};

const Navbar = (props: NavbarProps) => {
  const { isEmbedded } = useEmbed();

  if (isEmbedded) {
    return null;
  }

  return <NewNavbar {...props} />;
};

export default Navbar;
